import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { APP_VERSION } from '../../config';
import {
  Zap,
  Calculator,
  AlertTriangle,
  BookOpen,
  ArrowRight,
  Target,
  Settings,
  CheckCircle,
  Clock,
  Shield,
  Flame,
  Wind,
  RefreshCw,
  ShoppingBag,
  MessageSquare,
  Wrench,
  History,
  Star,
  Monitor,
  PenTool
} from 'lucide-react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import WorkspaceSetupBanner, { getWorkspaceStage, WorkspaceStatusLabel } from '@/components/WorkspaceSetupBanner';
import GatedToolCard from '@/components/GatedToolCard';

const LaserDashboard = () => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [lasers, setLasers] = useState([]);
  const [recentCalculations, setRecentCalculations] = useState([]);
  const [setupProgress, setSetupProgress] = useState({ hasLaser: false, hasCalculation: false });
  
  // Phase 3.0: Track workspace setup completion state
  const [workspaceReady, setWorkspaceReady] = useState(false);
  const prevWorkspaceReady = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const [lasersRes, historyRes] = await Promise.all([
          axios.get(`${API}/laser/machines`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] })),
          axios.get(`${API}/laser/history?limit=5`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] }))
        ]);
        
        const laserList = lasersRes.data || [];
        setLasers(laserList);
        setRecentCalculations(historyRes.data || []);
        
        // Calculate setup progress
        const hasLaser = laserList.length > 0;
        const hasCalculation = (historyRes.data || []).length > 0;
        
        setSetupProgress({ 
          hasLaser, 
          hasCalculation,
          laserName: laserList[0]?.name || 'No laser'
        });
        
        // Phase 3.0: Set workspace ready state
        setWorkspaceReady(hasLaser);
      } catch (error) {
        console.error('Failed to fetch laser data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  // Setup progress steps - MUST be declared before useEffect that references progressPercent
  const progressSteps = setupProgress.hasLaser ? [
    { label: 'Laser Added', done: setupProgress.hasLaser, link: '/laser/setup' },
    { label: 'First Calculation', done: setupProgress.hasCalculation, link: '/laser/calculator' },
  ] : [
    { label: 'Set Up Laser', done: false, link: '/laser/setup', primary: true },
    { label: 'First Calculation', done: false, link: '/laser/calculator' },
  ];
  const progressPercent = Math.round((progressSteps.filter(s => s.done).length / progressSteps.length) * 100);
  
  // Phase 3.1: Show toast when workspace becomes operational
  useEffect(() => {
    if (workspaceReady && !prevWorkspaceReady.current && progressPercent >= 100) {
      toast.success('Laser validated. Workspace operational.', {
        duration: 4000,
        icon: <CheckCircle className="w-5 h-5 text-emerald-400" />
      });
    }
    prevWorkspaceReady.current = workspaceReady;
  }, [workspaceReady, progressPercent]);

  // Advanced tools matching Router Edition structure
  const advancedTools = [
    {
      title: 'My Lasers',
      description: 'Manage your CO2 and diode laser profiles.',
      icon: Zap,
      link: '/laser/setup'
    },
    {
      title: 'Marketplace',
      description: 'Buy/sell laser equipment and materials.',
      icon: ShoppingBag,
      link: '/laser/marketplace'
    },
    {
      title: 'My Equipment',
      description: 'Your laser profile and account settings.',
      icon: Wrench,
      link: '/profile'
    },
    {
      title: 'Community',
      description: 'Discuss laser settings and safety tips.',
      icon: MessageSquare,
      link: '/community'
    }
  ];

  // Active laser for profile widget
  const activeLaser = lasers[0];

  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
      data-testid="laser-dashboard"
      style={bgStyle(IMAGES.LASER_DASHBOARD)}
    >
      {/* Dark Overlay */}
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />

      {/* Page Header - Matching Router */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">Foreman Console – Laser Operations</div>
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#e67e22] tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Work Session
            </h1>
            <p className="mt-2 text-sm md:text-base text-[#e67e22] max-w-lg">
              Configure → calculate → cut → finish. Welcome back, {user?.full_name}.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Phase 3.1: Workspace Setup Warning Banner or Operational Badge */}
        <WorkspaceSetupBanner 
          setupComplete={workspaceReady}
          completionPercent={progressPercent}
          setupLink="/laser/setup"
          discipline="laser"
        />
        
        {/* Setup Progress Banner - Industrial Stage Display */}
        {workspaceReady && progressPercent < 100 && (
          <div className="bg-black/80 backdrop-blur-sm border border-[#e67e22]/50 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-4">
                <Zap className="w-5 h-5 text-[#e67e22]" />
                <span className="text-white font-medium">
                  {setupProgress.laserName || 'Laser Setup'}
                </span>
                <WorkspaceStatusLabel percent={progressPercent} />
              </div>
              <div className="flex items-center gap-3">
                <Link to="/laser/setup">
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-7">
                    <ArrowRight className="w-3 h-3 mr-1" />
                    Continue Setup
                  </Button>
                </Link>
              </div>
            </div>
            <div className="w-full h-2 bg-black/70 rounded-full overflow-hidden mb-3">
              <div 
                className={`h-full bg-gradient-to-r from-[#e67e22] to-[#f39c12] transition-all duration-500 ${
                  progressPercent >= 100 ? 'shadow-[0_0_10px_rgba(16,185,129,0.5)]' : ''
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              {progressSteps.map((step, i) => (
                <Link key={i} to={step.link} className="flex items-center gap-2 group">
                  {step.done ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : step.primary ? (
                    <div className="w-4 h-4 border-2 border-emerald-500 rounded-full bg-emerald-500/20 group-hover:bg-emerald-500/40 animate-pulse" />
                  ) : (
                    <div className="w-4 h-4 border-2 border-[#3a3a3a] rounded-full group-hover:border-[#e67e22]" />
                  )}
                  <span className={`text-sm ${
                    step.done 
                      ? 'text-green-400 line-through' 
                      : step.primary 
                        ? 'text-emerald-400 font-medium group-hover:text-emerald-300' 
                        : 'text-[#d6d6d6] group-hover:text-[#e67e22]'
                  }`}>
                    {step.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Active Laser Profile Widget - Matching Router */}
        {activeLaser && (
          <div className="bg-black/80 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#e67e22]" />
                <span className="text-white font-medium">Active Laser Profile</span>
              </div>
              <Link to="/laser/setup">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#a3a3a3] hover:text-white h-7 px-2"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Update Setup
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="bg-black/70 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Laser</div>
                <div className="text-sm text-white font-medium truncate">{activeLaser.name}</div>
              </div>
              <div className="bg-black/70 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Type</div>
                <div className="text-sm text-white font-medium uppercase">{activeLaser.laser_type || 'CO2'}</div>
              </div>
              <div className="bg-black/70 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Power</div>
                <div className="text-sm text-white font-medium">{activeLaser.power_watts || 40}W</div>
              </div>
              <div className="bg-black/70 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Bed Size</div>
                <div className="text-sm text-white font-medium">
                  {activeLaser.bed_width || 300}x{activeLaser.bed_height || 200}mm
                </div>
              </div>
              <div className="bg-black/70 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Cooling</div>
                <div className="text-sm text-white font-medium capitalize">{activeLaser.cooling_type || 'Water'}</div>
              </div>
              <div className="bg-black/70 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Air Assist</div>
                <div className="text-sm text-white font-medium">{activeLaser.has_air_assist ? 'Yes' : 'No'}</div>
              </div>
            </div>
          </div>
        )}

        {/* Safety Banner */}
        <div className="bg-black/80 backdrop-blur-sm border border-red-500/40 rounded-sm p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-white font-medium mb-1">Safety First</div>
              <div className="text-xs text-[#d6d6d6] space-x-4">
                <span>• Always wear proper eye protection</span>
                <span>• Ensure adequate ventilation</span>
                <span>• Never leave laser unattended</span>
                <span>• Keep fire extinguisher nearby</span>
              </div>
            </div>
          </div>
        </div>

        {/* Core tools - Matching Router */}
        <div className="bg-black/80 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-6">
          <div className="mb-4">
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22]">Core tools</div>
            <div className="text-sm text-[#e0e0e0] mt-1">
              Operational tools used before and after a cut.
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Troubleshooting - Gated */}
            <GatedToolCard
              to="/laser/troubleshooting"
              setupComplete={workspaceReady}
              className="bg-black/70 border border-[#3a3a3a]/50 p-4 rounded-sm hover:border-[#e67e22]/50 transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-black/70 border border-[#3a3a3a]/50 flex items-center justify-center shrink-0 group-hover:border-[#e67e22]/50 transition-colors">
                  <AlertTriangle className="w-5 h-5 text-[#e67e22]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#e67e22] transition-colors">Troubleshooting</div>
                  <div className="text-xs text-[#e0e0e0] mt-1">Fix alignment, power, burning and focus issues.</div>
                </div>
              </div>
            </GatedToolCard>

            {/* Material Guide */}
            <Link to="/laser/materials" className="bg-black/70 border border-[#3a3a3a]/50 p-4 rounded-sm hover:border-[#e67e22]/50 transition-all group">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-black/70 border border-[#3a3a3a]/50 flex items-center justify-center shrink-0 group-hover:border-[#e67e22]/50 transition-colors">
                  <BookOpen className="w-5 h-5 text-[#e67e22]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white group-hover:text-[#e67e22] transition-colors">Material Guide</div>
                  <div className="text-xs text-[#e0e0e0] mt-1">Power &amp; speed settings for 30+ materials.</div>
                </div>
              </div>
            </Link>

            {/* Safety — prominent red border */}
            <Link to="/laser/safety" className="bg-black/70 border border-red-500/40 p-4 rounded-sm hover:border-red-400/70 transition-all group">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-red-500/10 border border-red-500/40 flex items-center justify-center shrink-0 group-hover:border-red-400/70 transition-colors">
                  <Shield className="w-5 h-5 text-red-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-red-400 transition-colors">Safety Guide</div>
                  <div className="text-xs text-[#e0e0e0] mt-1">Eye protection, fumes, fire, enclosures.</div>
                </div>
              </div>
            </Link>

            {/* Design File Prep */}
            <Link to="/laser/design-prep" className="bg-black/70 border border-[#3a3a3a]/50 p-4 rounded-sm hover:border-[#e67e22]/50 transition-all group">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-black/70 border border-[#3a3a3a]/50 flex items-center justify-center shrink-0 group-hover:border-[#e67e22]/50 transition-colors">
                  <PenTool className="w-5 h-5 text-[#e67e22]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white group-hover:text-[#e67e22] transition-colors">Design File Prep</div>
                  <div className="text-xs text-[#e0e0e0] mt-1">SVG, DXF, kerf compensation, LightBurn layers.</div>
                </div>
              </div>
            </Link>

            {/* Software Guide */}
            <Link to="/laser/software-guide" className="bg-black/70 border border-[#3a3a3a]/50 p-4 rounded-sm hover:border-[#e67e22]/50 transition-all group">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-black/70 border border-[#3a3a3a]/50 flex items-center justify-center shrink-0 group-hover:border-[#e67e22]/50 transition-colors">
                  <Monitor className="w-5 h-5 text-[#e67e22]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white group-hover:text-[#e67e22] transition-colors">Software Guide</div>
                  <div className="text-xs text-[#e0e0e0] mt-1">LightBurn, LaserGRBL, xTool CS, RDWorks.</div>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Support & advanced - Matching Router */}
        <div className="bg-black/80 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-6">
          <div className="mb-4">
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22]">Support & advanced</div>
            <div className="text-sm text-[#e0e0e0] mt-1">
              Secondary tools for sourcing, equipment, and discussion.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {advancedTools.map((tool, index) => {
              const Icon = tool.icon;
              return (
                <Link
                  key={index}
                  to={tool.link}
                  className="bg-black/70 border border-[#3a3a3a]/50 p-4 rounded-sm hover:border-[#e67e22]/50 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-black/70 border border-[#3a3a3a]/50 flex items-center justify-center shrink-0 group-hover:border-[#e67e22]/50 transition-colors">
                      <Icon className="w-5 h-5 text-[#e67e22]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white group-hover:text-[#e67e22] transition-colors">
                        {tool.title}
                      </div>
                      <div className="text-xs text-[#e0e0e0] mt-1">
                        {tool.description}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Recent Activity Feed - Matching Router */}
        <div className="bg-black/80 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] flex items-center gap-2">
                <History className="w-4 h-4" />
                Recent Activity
              </div>
              <div className="text-sm text-[#e0e0e0] mt-1">
                Pick up where you left off
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Recent Calculations */}
            <div className="bg-black/80 border border-[#3a3a3a]/30 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="w-4 h-4 text-[#3b82f6]" />
                <span className="text-sm font-medium text-white">Recent Calculations</span>
              </div>
              {recentCalculations.length > 0 ? (
                <div className="space-y-2">
                  {recentCalculations.slice(0, 3).map((calc, i) => (
                    <Link
                      key={i}
                      to="/laser/calculator"
                      className="block p-2 bg-black/20 rounded hover:bg-[#3b82f6]/10 hover:border-[#3b82f6]/30 border border-transparent transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs font-medium text-white truncate capitalize">{calc.material}</div>
                          <div className="text-xs text-[#a3a3a3]">{calc.power}% · {calc.speed}mm/s</div>
                        </div>
                        <div className="text-xs text-[#6b7280] flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(calc.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-[#6b7280] py-4 text-center">
                  No calculations yet.{' '}
                  <Link to="/laser/calculator" className="text-[#3b82f6] hover:underline">Start one</Link>
                </div>
              )}
            </div>

            {/* My Lasers */}
            <div className="bg-black/80 border border-[#3a3a3a]/30 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-[#f59e0b]" />
                <span className="text-sm font-medium text-white">My Lasers</span>
              </div>
              {lasers.length > 0 ? (
                <div className="space-y-2">
                  {lasers.slice(0, 3).map((laser, i) => (
                    <Link
                      key={i}
                      to="/laser/setup"
                      className="block p-2 bg-black/20 rounded hover:bg-[#f59e0b]/10 hover:border-[#f59e0b]/30 border border-transparent transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs font-medium text-[#f59e0b] truncate">{laser.name}</div>
                          <div className="text-xs text-[#a3a3a3]">{laser.laser_type?.toUpperCase()} · {laser.power_watts}W</div>
                        </div>
                        <Star className="w-3 h-3 text-[#f59e0b]" />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-[#6b7280] py-4 text-center">
                  No lasers configured.{' '}
                  <Link to="/laser/setup" className="text-[#f59e0b] hover:underline">Add one</Link>
                </div>
              )}
            </div>

            {/* Quick Material Reference */}
            <div className="bg-black/80 border border-[#3a3a3a]/30 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-4 h-4 text-[#10b981]" />
                <span className="text-sm font-medium text-white">Quick Reference (40W CO2)</span>
              </div>
              <div className="space-y-2">
                {[
                  { material: 'Plywood 3mm', power: '80%', speed: '10mm/s' },
                  { material: 'Acrylic 3mm', power: '70%', speed: '8mm/s' },
                  { material: 'Leather', power: '15%', speed: '200mm/s' },
                ].map((mat, i) => (
                  <div key={i} className="p-2 bg-black/20 rounded">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-[#10b981]">{mat.material}</span>
                      <span className="text-xs text-[#a3a3a3]">{mat.power} · {mat.speed}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Bar - Matching Router */}
      <div className="bg-black/80 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-4 mt-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#27ae60] rounded-full animate-pulse" />
              <span className="text-xs text-[#d6d6d6]">System online</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#27ae60] rounded-full" />
              <span className="text-xs text-[#d6d6d6]">Database connected</span>
            </div>
          </div>
          <div className="text-xs font-mono text-[#a3a3a3]">LASER CUTTING EDITION v{APP_VERSION}</div>
        </div>
      </div>
    </div>
  );
};

export default LaserDashboard;
